import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";

import { Wallet } from "../../../services/queries/wallets/types";
import { formatMoney } from "../../../utils/money";
import Flag from "../../Flag";

interface BalanceHeroProps {
  wallet?: Wallet | undefined;
  loading?: boolean;
  refreshing?: boolean;
  onAdd: (walletId: string, currency: string) => void;
  onSend: () => void;
  onRefresh: () => void;
}

export default function BalanceHero({
  wallet,
  loading = false,
  refreshing = false,
  onAdd,
  onSend,
  onRefresh,
}: BalanceHeroProps) {
  const [hidden, setHidden] = useState(false);

  if (loading || !wallet) {
    return (
      <div className="rounded-2xl bg-primary p-6 md:p-8 min-h-[220px]">
        <div className="h-4 w-24 rounded animate-shimmer bg-1000px bg-shimmer" />
        <div className="mt-6 h-10 w-56 rounded animate-shimmer bg-1000px bg-shimmer" />
        <div className="mt-8 flex gap-3">
          <div className="h-11 w-32 rounded-xl animate-shimmer bg-1000px bg-shimmer" />
          <div className="h-11 w-32 rounded-xl animate-shimmer bg-1000px bg-shimmer" />
        </div>
      </div>
    );
  }

  const balance = formatMoney(wallet.availableBalance, wallet.currencySymbol);

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 md:p-8 text-white shadow-sm"
    >
      {/* decorative accent glow */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-secondary/20 blur-2xl" />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-white/80">
          <Flag currency={wallet.currencyCode} className="h-5 w-5" />
          <span className="font-medium">{wallet.currencyCode}</span>
          <span className="text-white/50">· Available balance</span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setHidden((prev) => !prev)}
            className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            aria-label={hidden ? "Show balance" : "Hide balance"}
          >
            {hidden ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-60"
            aria-label="Refresh balance"
          >
            <ArrowPathIcon
              className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      <div className="relative mt-4">
        <div className="text-4xl md:text-5xl font-semibold tracking-tight">
          {hidden ? <span className="tracking-widest">••••••••</span> : balance}
        </div>
      </div>

      <div className="relative mt-8 flex flex-wrap gap-3">
        {wallet.canDeposit && (
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => onAdd(wallet.id, wallet.currencyCode)}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-primary hover:bg-white/90 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Add Money
          </motion.button>
        )}

        {wallet.canWithdraw && (
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={onSend}
            className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
            Send Money
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}
